// Translation
import { useTranslationContext } from '../../../../context/TranslationContext';
// Components/ui
import { MenuItem, Checkbox, SelectChangeEvent, FormControlLabel, Typography } from '@mui/material';
import {
  StyledReminderItemContainer,
  StyledReminderInputsContainer,
  StyledReminderTextField,
  StyledReminderSelect,
  StyledReminderActionsContainer,
  StyledReminderDeleteIconButton,
  StyledReminderItemViewContainer
} from './style';
import DeleteIcon from '@mui/icons-material/Delete';
// Utils
import { selectOptions } from './utils/selectOptions';
// Types
import { ChangeEvent } from 'react';
import { EventNotificationFront } from '../../../../types';
// Context

type ReminderItemProps = {
  data: EventNotificationFront;
  timeValueHandler: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    notificationId: string,
  ) => void;
  timePeriodHandler: (event: SelectChangeEvent<unknown>, notificationId: string) => void;
  sendToContactHandler: (event: ChangeEvent<HTMLInputElement>, notificationId: string) => void;
  deleteHandler: (notificationId: string) => void;
  disabled?: boolean;
};

const ReminderItem = ({
  data,
  timeValueHandler,
  timePeriodHandler,
  sendToContactHandler,
  deleteHandler,
  disabled = false,
}: ReminderItemProps) => {
  // const { isEditing, showDetails } = useContext(ActivitiesCardContext);
  const t = useTranslationContext();

  // Asegurar que timeValue nunca sea undefined para evitar errores de controlled/uncontrolled
  const safeTimeValue = data.timeValue ?? 0;

  // Si está en modo view (disabled), mostrar los datos de forma estática
  if (disabled) {
    return (
      <StyledReminderItemViewContainer>
        <StyledReminderInputsContainer>
          <Typography variant="body1" sx={{color: 'text.secondary'}}>
            {t('calendarView.reminders.sendReminder.pt1')} {safeTimeValue}{' '}
            {t(`calendarView.reminders.timePeriod.${selectOptions.find((opt) => opt.value === data.timePeriod)?.title || 'minutes'}`)}{' '}
            {t('calendarView.reminders.sendReminder.pt2')}
          </Typography>
        </StyledReminderInputsContainer>
        {data.sendToContact && (
          <Typography variant="body1" sx={{ mt: 0.5, fontSize: '0.875rem', color: 'text.secondary' }}>
            *{t('calendarView.reminders.sendReminderToContact')}
          </Typography>
        )}
      </StyledReminderItemViewContainer>
    );
  }

  return (
    <StyledReminderItemContainer>
      <StyledReminderInputsContainer>
        <Typography variant="body2" sx={{ userSelect: 'none' }}>
          {t('calendarView.reminders.sendReminder.pt1')}
        </Typography>
        <StyledReminderTextField
          disabled={disabled}
          onChange={(e) => timeValueHandler(e, data.id)}
          value={safeTimeValue}
          type="text"
          variant="outlined"
          size="small"
        />
        <StyledReminderSelect
          disabled={disabled}
          onChange={(e) => timePeriodHandler(e, data.id)}
          value={data.timePeriod}
          variant="outlined"
          size="small"
          MenuProps={{
            PaperProps: {
              sx: {
                //@ts-ignore
                backgroundColor: (theme) => theme.palette.background.panel,
              },
            },
          }}
        >
          {selectOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(`calendarView.reminders.timePeriod.${option.title}`)}
            </MenuItem>
          ))}
        </StyledReminderSelect>
        <Typography variant="body2" sx={{ userSelect: 'none' }}>
          {t('calendarView.reminders.sendReminder.pt2')}
        </Typography>
      </StyledReminderInputsContainer>
      <StyledReminderActionsContainer>
        <FormControlLabel
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
          disabled={disabled}
          label={t(`calendarView.reminders.sendReminderToContact`)}
          control={
            <Checkbox
              checked={data.sendToContact}
              onChange={(e) => sendToContactHandler(e, data.id)}
              color="secondary"
              disabled={disabled}
            />
          }
        />
        <StyledReminderDeleteIconButton
          disabled={disabled}
          onClick={() => deleteHandler(data.id)}
          sx={{
            ml: 'auto',
          }}
        >
          <DeleteIcon />
        </StyledReminderDeleteIconButton>
      </StyledReminderActionsContainer>
    </StyledReminderItemContainer>
  );
};

export default ReminderItem;
